import React, { createContext, useState, useContext, useEffect } from 'react';
import i18next from 'i18next';

const CityContext = createContext();

// Cities configuration with currency information
export const CITIES = {
  BARCELONA: {
    id: 'barcelona',
    name: 'Barcelona',
    flag: '🇪🇸',
    country: 'Spain',
    currency: 'EUR',
    currencySymbol: '€',
    language: 'es',
    // Rate compared to EUR (1 EUR = 1 EUR)
    exchangeRate: 1,
    coordinates: {
      latitude: 41.3851,
      longitude: 2.1734
    }
  },
  MEXICO_CITY: {
    id: 'mexico_city',
    name: 'Ciudad de México',
    flag: '🇲🇽',
    country: 'Mexico',
    currency: 'MXN',
    currencySymbol: '$',
    language: 'es',
    // Example rate: 1 EUR ≈ 18 MXN (this should be updated with correct rate)
    exchangeRate: 18,
    coordinates: {
      latitude: 19.4326,
      longitude: -99.1332
    }
  },
  SANTIAGO: {
    id: 'santiago',
    name: 'Santiago de Chile',
    flag: '🇨🇱',
    country: 'Chile',
    currency: 'CLP',
    currencySymbol: '$',
    language: 'es',
    // Example rate: 1 EUR ≈ 1000 CLP (this should be updated with correct rate)
    exchangeRate: 1000,
    coordinates: {
      latitude: -33.4489,
      longitude: -70.6693
    }
  },
  CARACAS: {
    id: 'caracas',
    name: 'Caracas',
    flag: '🇻🇪',
    country: 'Venezuela',
    currency: 'USD',
    currencySymbol: '$',
    language: 'es',
    // Exchange rate: 1 EUR ≈ 1.08 USD (adjust to current rate as needed)
    exchangeRate: 1.08,
    coordinates: {
      latitude: 10.4806,
      longitude: -66.9036
    }
  }
};

// Countries mapping for fallback city selection
const COUNTRY_FALLBACKS = {
  'Spain': 'barcelona',
  'Mexico': 'mexico_city',
  'Chile': 'santiago',
  'Venezuela': 'caracas',
  // Default fallback
  'default': 'barcelona'
};

// Language mappings for countries
const COUNTRY_LANGUAGES = {
  'Spain': 'es',
  'Mexico': 'es',
  'Chile': 'es',
  'Venezuela': 'es',
  'United States': 'en',
  'United Kingdom': 'en',
  'Canada': 'en',
  'Australia': 'en',
  // Add more countries and their primary languages as needed
  'default': 'en'
};

export function CityProvider({ children }) {
  // Default city is Barcelona
  const [currentCity, setCurrentCity] = useState(CITIES.BARCELONA);
  const [locationDetected, setLocationDetected] = useState(false);

  // Calculate distance between two coordinates using Haversine formula
  const getDistanceFromLatLonInKm = (lat1, lon1, lat2, lon2) => {
    const R = 6371; // Radius of the earth in km
    const dLat = deg2rad(lat2 - lat1);
    const dLon = deg2rad(lon2 - lon1);
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) * 
      Math.sin(dLon/2) * Math.sin(dLon/2); 
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a)); 
    const d = R * c; // Distance in km
    return d;
  }
  
  const deg2rad = (deg) => {
    return deg * (Math.PI/180)
  }

  // Find the nearest city to the user's location
  const findNearestCity = (userLat, userLon) => {
    let nearestCity = null;
    let minDistance = Infinity;
    
    Object.values(CITIES).forEach(city => {
      const distance = getDistanceFromLatLonInKm(
        userLat, userLon, 
        city.coordinates.latitude, city.coordinates.longitude
      );
      
      if (distance < minDistance) {
        minDistance = distance;
        nearestCity = city;
      }
    });
    
    return nearestCity;
  }

  // Find a city in the user's country or use default
  const findCityByCountry = (country) => {
    const cityId = COUNTRY_FALLBACKS[country] || COUNTRY_FALLBACKS.default;
    return Object.values(CITIES).find(city => city.id === cityId) || CITIES.BARCELONA;
  }

  // Get the appropriate language for a country
  const getLanguageForCountry = (country) => {
    return COUNTRY_LANGUAGES[country] || COUNTRY_LANGUAGES.default;
  }

  // Set the interface language
  const setAppLanguage = (language) => {
    if (language) {
      console.log('Setting language to:', language);
      i18next.changeLanguage(language);
    }
  }

  // Detect user's location on component mount
  useEffect(() => {
    if (!locationDetected && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          const nearestCity = findNearestCity(latitude, longitude);
          if (nearestCity) {
            setCurrentCity(nearestCity);
            // Set language based on detected city
            setAppLanguage(nearestCity.language);
            setLocationDetected(true);
          }
        },
        async (error) => {
          console.warn("Geolocation error:", error);
          try {
            // Fallback to IP-based geolocation
            const response = await fetch('https://ipapi.co/json/');
            const data = await response.json();
            if (data && data.country_name) {
              const cityByCountry = findCityByCountry(data.country_name);
              setCurrentCity(cityByCountry);
              
              // Set language based on country
              const language = getLanguageForCountry(data.country_name);
              setAppLanguage(language);
            }
          } catch (ipError) {
            console.warn("IP geolocation failed:", ipError);
            // Keep default city
          } finally {
            setLocationDetected(true);
          }
        },
        { timeout: 5000, maximumAge: 0 }
      );
    }
  }, [locationDetected]);

  const changeCity = (cityId) => {
    const city = Object.values(CITIES).find(city => city.id === cityId);
    if (city) {
      setCurrentCity(city);
      // Also update language when city is manually changed
      setAppLanguage(city.language);
    }
  };

  // Convert price from EUR to the selected city's currency
  const convertPrice = (priceInEur) => {
    // Handle comma as decimal separator (common in European format)
    const normalizedPrice = typeof priceInEur === 'string' 
      ? parseFloat(priceInEur.replace(',', '.')) 
      : priceInEur;
    
    if (isNaN(normalizedPrice)) return priceInEur; // Return original if not a valid number

    // Convert to the selected city's currency
    const convertedPrice = normalizedPrice * currentCity.exchangeRate;
    
    // Format based on currency
    if (currentCity.currency === 'EUR') {
      return convertedPrice.toFixed(2).replace('.', ',');
    } else if (currentCity.currency === 'CLP') {
      // Chilean Peso typically doesn't use decimals
      return Math.round(convertedPrice).toLocaleString();
    } else if (currentCity.currency === 'USD') {
      // US Dollars use point as decimal separator and 2 decimal places
      return convertedPrice.toFixed(2);
    }
    
    return convertedPrice.toString();
  };

  return (
    <CityContext.Provider value={{ currentCity, changeCity, convertPrice, CITIES, locationDetected }}>
      {children}
    </CityContext.Provider>
  );
}

export function useCity() {
  return useContext(CityContext);
} 